import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft,
  BookOpen,
  DollarSign,
  ListChecks,
  Save,
  Eye,
  Send,
  Plus,
  X,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Trash2,
  FileText,
  Play,
  Settings,
  CheckCircle,
  Clock,
  Archive,
  ShieldAlert,
} from 'lucide-react';
import { coursesApi, api, uploadsApi } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import MediaUploader from '../../components/MediaUploader';
import toast from 'react-hot-toast';

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface Lesson {
  id: string;
  title: string;
  description?: string;
  type: 'VIDEO' | 'DOCUMENT' | 'QUIZ';
  contentUrl?: string;
  duration?: number;
  order: number;
  isFree: boolean;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
  lessons: Lesson[];
}

interface Course {
  id: string;
  title: string;
  slug: string;
  description: string;
  shortDescription?: string;
  thumbnailUrl?: string;
  previewVideoUrl?: string;
  price: number;
  currency: string;
  status: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'PUBLISHED' | 'ARCHIVED';
  level: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
  language: string;
  requirements: string[];
  whatYouWillLearn: string[];
  categoryId: string;
  totalLessons: number;
  totalStudents: number;
  category?: Category;
  sections?: Section[];
}

type Tab = 'details' | 'content' | 'pricing' | 'settings';

const LEVELS = [
  { value: 'BEGINNER', label: 'Beginner' },
  { value: 'INTERMEDIATE', label: 'Intermediate' },
  { value: 'ADVANCED', label: 'Advanced' },
];

const LANGUAGES = [
  'English', 'Spanish', 'French', 'German', 'Portuguese', 
  'Italian', 'Russian', 'Chinese', 'Japanese', 'Korean',
  'Arabic', 'Hindi', 'Dutch', 'Polish', 'Turkish'
];

export default function EditCoursePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('details');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // SECURITY: Check professor verification status
  const isVerified = user?.verificationStatus === 'VERIFIED';
  
  // Form states
  const [formData, setFormData] = useState<Partial<Course>>({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Section/Lesson modals
  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState<string | null>(null);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  const [newLesson, setNewLesson] = useState<{
    title: string;
    description: string;
    type: 'VIDEO' | 'DOCUMENT' | 'QUIZ';
    contentUrl: string;
    duration: number;
    isFree: boolean;
  }>({
    title: '',
    description: '',
    type: 'VIDEO',
    contentUrl: '',
    duration: 0,
    isFree: false
  });

  // Requirements and learning points
  const [newRequirement, setNewRequirement] = useState('');
  const [newLearningPoint, setNewLearningPoint] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        const [courseRes, categoriesRes] = await Promise.all([
          coursesApi.getById(id, true),
          coursesApi.getCategories()
        ]);
        
        if (courseRes.data.success) {
          const courseData = courseRes.data.data as Course;
          setCourse(courseData);
          setFormData(courseData);
          
          // Expand first section by default
          if (courseData.sections && courseData.sections.length > 0) {
            setExpandedSections(new Set([courseData.sections[0].id]));
          }
        }
        
        if (categoriesRes.data.success) {
          setCategories(categoriesRes.data.data as Category[]);
        }
      } catch (error) {
        console.error('Failed to fetch course:', error);
        toast.error('Failed to load course');
        navigate('/professor/courses');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [id, navigate]);

  const handleInputChange = (field: keyof Course, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!id || !hasChanges) return;
    
    setIsSaving(true);
    try {
      const updateData = {
        title: formData.title,
        description: formData.description,
        shortDescription: formData.shortDescription || null,
        thumbnailUrl: formData.thumbnailUrl || null,
        previewVideoUrl: formData.previewVideoUrl || null,
        price: Number(formData.price),
        currency: formData.currency,
        categoryId: formData.categoryId,
        level: formData.level,
        language: formData.language,
        requirements: formData.requirements,
        whatYouWillLearn: formData.whatYouWillLearn,
      };
      
      const res = await coursesApi.update(id, updateData);
      
      if (res.data.success) {
        toast.success('Course saved successfully!');
        setHasChanges(false);
        setCourse(res.data.data as Course);
      }
    } catch (error) {
      console.error('Failed to save course:', error);
      toast.error('Failed to save course');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!id) return;
    
    // SECURITY: Check verification status before submitting
    if (!isVerified) {
      toast.error('Please verify your identity before submitting courses for review');
      navigate('/professor/verification');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const res = await api.post(`/api/courses/${id}/submit-for-review`);
      if (res.data.success) {
        toast.success('Course submitted for review! We\'ll notify you once it\'s approved.');
        setCourse(prev => prev ? { ...prev, status: 'PENDING_REVIEW' as Course['status'] } : null);
      }
    } catch (error: unknown) {
      console.error('Failed to submit course for review:', error);
      // Check for verification error from backend
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { code?: string; error?: string } } };
        if (axiosError.response?.data?.code === 'PROFESSOR_NOT_VERIFIED') {
          toast.error('Identity verification required. Redirecting to verification page...');
          navigate('/professor/verification');
          return;
        }
        toast.error(axiosError.response?.data?.error || 'Failed to submit course for review');
      } else {
        toast.error('Failed to submit course for review');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSection = async () => {
    if (!id || !newSectionTitle.trim()) return;
    
    try {
      const sectionOrder = course?.sections?.length || 0;
      const res = await api.post(`/api/courses/${id}/sections`, {
        title: newSectionTitle.trim(),
        description: newSectionDescription.trim() || null,
        order: sectionOrder
      });
      
      if (res.data.success) {
        toast.success('Section added!');
        setNewSectionTitle('');
        setNewSectionDescription('');
        setShowAddSection(false);
        
        // Refresh course data
        const courseRes = await coursesApi.getById(id, true);
        if (courseRes.data.success) {
          setCourse(courseRes.data.data as Course);
        }
      }
    } catch (error) {
      console.error('Failed to add section:', error);
      toast.error('Failed to add section');
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this section and all its lessons?')) return;
    
    try {
      await api.delete(`/api/courses/${id}/sections/${sectionId}`);
      toast.success('Section deleted!');
      
      // Refresh course data
      const courseRes = await coursesApi.getById(id, true);
      if (courseRes.data.success) {
        setCourse(courseRes.data.data as Course);
      }
    } catch (error) {
      console.error('Failed to delete section:', error);
      toast.error('Failed to delete section');
    }
  };

  const handleAddLesson = async (sectionId: string) => {
    if (!id || !newLesson.title.trim()) return;
    
    try {
      const section = course?.sections?.find(s => s.id === sectionId);
      const lessonOrder = section?.lessons?.length || 0;
      
      const res = await api.post(`/api/courses/${id}/sections/${sectionId}/lessons`, {
        title: newLesson.title.trim(),
        description: newLesson.description.trim() || null,
        type: newLesson.type,
        contentUrl: newLesson.contentUrl.trim() || null,
        duration: newLesson.duration || null,
        order: lessonOrder,
        isFree: newLesson.isFree
      });
      
      if (res.data.success) {
        toast.success('Lesson added!');
        setNewLesson({
          title: '',
          description: '',
          type: 'VIDEO',
          contentUrl: '',
          duration: 0,
          isFree: false
        });
        setShowAddLesson(null);
        
        // Refresh course data
        const courseRes = await coursesApi.getById(id, true);
        if (courseRes.data.success) {
          setCourse(courseRes.data.data as Course);
        }
      }
    } catch (error) {
      console.error('Failed to add lesson:', error);
      toast.error('Failed to add lesson');
    }
  };

  const handleDeleteLesson = async (sectionId: string, lessonId: string) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    
    try {
      await api.delete(`/api/courses/${id}/sections/${sectionId}/lessons/${lessonId}`);
      toast.success('Lesson deleted!');
      
      // Refresh course data
      const courseRes = await coursesApi.getById(id, true);
      if (courseRes.data.success) {
        setCourse(courseRes.data.data as Course);
      }
    } catch (error) {
      console.error('Failed to delete lesson:', error);
      toast.error('Failed to delete lesson');
    }
  };

  const handleAddRequirement = () => {
    if (newRequirement.trim() && (formData.requirements?.length || 0) < 20) {
      handleInputChange('requirements', [...(formData.requirements || []), newRequirement.trim()]);
      setNewRequirement('');
    }
  };

  const handleRemoveRequirement = (index: number) => {
    handleInputChange('requirements', formData.requirements?.filter((_, i) => i !== index) || []);
  };

  const handleAddLearningPoint = () => {
    if (newLearningPoint.trim() && (formData.whatYouWillLearn?.length || 0) < 20) {
      handleInputChange('whatYouWillLearn', [...(formData.whatYouWillLearn || []), newLearningPoint.trim()]);
      setNewLearningPoint('');
    }
  };

  const handleRemoveLearningPoint = (index: number) => {
    handleInputChange('whatYouWillLearn', formData.whatYouWillLearn?.filter((_, i) => i !== index) || []);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'Published' };
      case 'DRAFT':
        return { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'Draft' };
      case 'PENDING_REVIEW':
        return { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Pending Review' };
      case 'APPROVED':
        return { icon: CheckCircle, color: 'text-cyan-400', bg: 'bg-cyan-500/20', label: 'Approved' };
      case 'REJECTED':
        return { icon: ShieldAlert, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Rejected' };
      case 'ARCHIVED':
        return { icon: Archive, color: 'text-white/40', bg: 'bg-white/10', label: 'Archived' };
      default:
        return { icon: Clock, color: 'text-white/40', bg: 'bg-white/10', label: status };
    }
  };

  const getLessonTypeIcon = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return Play;
      case 'DOCUMENT':
        return FileText;
      case 'QUIZ':
        return ListChecks;
      default:
        return FileText;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="skeleton h-20 rounded-2xl" />
        <div className="skeleton h-96 rounded-2xl" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="max-w-6xl mx-auto text-center py-16">
        <h2 className="text-2xl font-bold text-white mb-4">Course not found</h2>
        <Link to="/professor/courses" className="btn-primary">
          Back to Courses
        </Link>
      </div>
    );
  }

  const statusConfig = getStatusConfig(course.status);
  const StatusIcon = statusConfig.icon;

  const tabs = [
    { id: 'details' as Tab, label: 'Details', icon: BookOpen },
    { id: 'content' as Tab, label: 'Content', icon: ListChecks },
    { id: 'pricing' as Tab, label: 'Pricing', icon: DollarSign },
    { id: 'settings' as Tab, label: 'Settings', icon: Settings },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <button
          onClick={() => navigate('/professor/courses')}
          className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Courses</span>
        </button>
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {course.thumbnailUrl ? (
              <img
                src={course.thumbnailUrl}
                alt={course.title}
                className="w-16 h-16 rounded-xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-white/30" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white line-clamp-1">{course.title}</h1>
              <div className="flex items-center gap-3 mt-1">
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${statusConfig.bg}`}>
                  <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                  <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                </div>
                <span className="text-white/40 text-sm">{course.totalLessons} lessons</span>
                <span className="text-white/40 text-sm">{course.totalStudents} students</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Link
              to={`/courses/${course.slug}`}
              className="px-4 py-2.5 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </Link>
            {course.status === 'DRAFT' && (
              isVerified ? (
                <button
                  onClick={handleSubmitForReview}
                  disabled={isSubmitting}
                  className="px-4 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Submit for Review
                </button>
              ) : (
                <Link
                  to="/professor/verification"
                  className="px-4 py-2.5 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-2"
                >
                  <ShieldAlert className="w-4 h-4" />
                  Verify to Submit
                </Link>
              )
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-2 mb-6 overflow-x-auto scrollbar-hide"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all duration-200 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white/10 text-white'
                : 'text-white/50 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="font-medium">{tab.label}</span>
          </button>
        ))}
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-6 lg:p-8"
        style={{
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(15, 16, 20, 0.95) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.06)'
        }}
      >
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            <div>
              <label className="label">Course Title *</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Complete Web Development Bootcamp"
                className="input"
              />
            </div>

            <div>
              <label className="label">Short Description</label>
              <input
                type="text"
                value={formData.shortDescription || ''}
                onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                placeholder="A brief summary of your course"
                maxLength={500}
                className="input"
              />
            </div>

            <div>
              <label className="label">Full Description *</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Provide a detailed description..."
                rows={6}
                className="input resize-none"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="label">Category</label>
                <select
                  value={formData.categoryId || ''}
                  onChange={(e) => handleInputChange('categoryId', e.target.value)}
                  className="input"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Level</label>
                <select
                  value={formData.level || 'BEGINNER'}
                  onChange={(e) => handleInputChange('level', e.target.value)}
                  className="input"
                >
                  {LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Language</label>
                <select
                  value={formData.language || 'English'}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                  className="input"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Media */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label">Course Thumbnail</label>
                <MediaUploader
                  type="thumbnail"
                  value={formData.thumbnailUrl || ''}
                  onChange={(url) => handleInputChange('thumbnailUrl', url)}
                  onUpload={async (file) => {
                    const res = await uploadsApi.uploadThumbnail(id!, file);
                    if (res.success && res.data) {
                      toast.success('Thumbnail uploaded!');
                      return { url: res.data.url };
                    }
                    throw new Error('Upload failed');
                  }}
                  courseId={id}
                />
              </div>
              <div>
                <label className="label">Preview Video</label>
                <MediaUploader
                  type="preview-video"
                  value={formData.previewVideoUrl || ''}
                  onChange={(url) => handleInputChange('previewVideoUrl', url)}
                  onUpload={async (file) => {
                    const res = await uploadsApi.uploadPreviewVideo(id!, file);
                    if (res.success && res.data) {
                      toast.success('Preview video uploaded!');
                      return { url: res.data.url };
                    }
                    throw new Error('Upload failed');
                  }}
                  courseId={id}
                />
              </div>
            </div>

            {/* Requirements */}
            <div>
              <label className="label">Requirements</label>
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddRequirement()}
                  placeholder="Add a requirement..."
                  className="input flex-1"
                />
                <button
                  onClick={handleAddRequirement}
                  disabled={!newRequirement.trim()}
                  className="px-4 rounded-xl bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                {formData.requirements?.map((req, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 group">
                    <span className="text-white/70 flex-1">{req}</span>
                    <button
                      onClick={() => handleRemoveRequirement(index)}
                      className="text-white/30 hover:text-coral transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* What You'll Learn */}
            <div>
              <label className="label">What Students Will Learn</label>
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newLearningPoint}
                  onChange={(e) => setNewLearningPoint(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddLearningPoint()}
                  placeholder="Add a learning outcome..."
                  className="input flex-1"
                />
                <button
                  onClick={handleAddLearningPoint}
                  disabled={!newLearningPoint.trim()}
                  className="px-4 rounded-xl bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                {formData.whatYouWillLearn?.map((point, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 group">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-white/70 flex-1">{point}</span>
                    <button
                      onClick={() => handleRemoveLearningPoint(index)}
                      className="text-white/30 hover:text-coral transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Course Content</h3>
                <p className="text-white/50 text-sm">Add sections and lessons to your course</p>
              </div>
              <button
                onClick={() => setShowAddSection(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Section
              </button>
            </div>

            {/* Sections List */}
            {course.sections && course.sections.length > 0 ? (
              <div className="space-y-4">
                {course.sections.map((section, sectionIndex) => (
                  <div
                    key={section.id}
                    className="rounded-xl overflow-hidden"
                    style={{
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.06)'
                    }}
                  >
                    {/* Section Header */}
                    <div
                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                      onClick={() => toggleSection(section.id)}
                    >
                      <GripVertical className="w-4 h-4 text-white/20" />
                      <button className="p-1">
                        {expandedSections.has(section.id) ? (
                          <ChevronDown className="w-4 h-4 text-white/50" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-white/50" />
                        )}
                      </button>
                      <div className="flex-1">
                        <h4 className="font-medium text-white">
                          Section {sectionIndex + 1}: {section.title}
                        </h4>
                        <p className="text-sm text-white/40">
                          {section.lessons?.length || 0} lessons
                          {section.description && ` • ${section.description}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAddLesson(section.id);
                          }}
                          className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSection(section.id);
                          }}
                          className="p-2 rounded-lg text-white/40 hover:text-coral hover:bg-coral/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Lessons */}
                    <AnimatePresence>
                      {expandedSections.has(section.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-white/5"
                        >
                          {section.lessons && section.lessons.length > 0 ? (
                            <div className="p-2">
                              {section.lessons.map((lesson, lessonIndex) => {
                                const LessonIcon = getLessonTypeIcon(lesson.type);
                                return (
                                  <div
                                    key={lesson.id}
                                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.03] transition-colors group"
                                  >
                                    <GripVertical className="w-4 h-4 text-white/10" />
                                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                                      <LessonIcon className="w-4 h-4 text-white/40" />
                                    </div>
                                    <div className="flex-1">
                                      <span className="text-white/70">{lessonIndex + 1}. {lesson.title}</span>
                                      <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40">
                                        <span>{lesson.type.toLowerCase()}</span>
                                        {lesson.duration && <span>{Math.round(lesson.duration / 60)} min</span>}
                                        {lesson.isFree && (
                                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Free</span>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => handleDeleteLesson(section.id, lesson.id)}
                                      className="p-2 rounded-lg text-white/20 hover:text-coral hover:bg-coral/10 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="p-6 text-center">
                              <p className="text-white/40 mb-3">No lessons in this section</p>
                              <button
                                onClick={() => setShowAddLesson(section.id)}
                                className="text-sm font-medium text-coral hover:text-coral/80 transition-colors"
                              >
                                Add your first lesson
                              </button>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <ListChecks className="w-8 h-8 text-white/30" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No content yet</h3>
                <p className="text-white/50 mb-6">Start by adding your first section</p>
                <button
                  onClick={() => setShowAddSection(true)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Section
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label">Price (USD)</label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="number"
                    value={formData.price || 0}
                    onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="9999.99"
                    step="0.01"
                    className="input pl-12"
                  />
                </div>
                <p className="text-white/30 text-xs mt-2">Set to 0 for a free course</p>
              </div>

              <div>
                <label className="label">Currency</label>
                <select
                  value={formData.currency || 'USD'}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="input"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>
            </div>

            {/* Earnings Breakdown */}
            {(formData.price || 0) > 0 && (
              <div className="p-6 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                  Earnings Breakdown (per enrollment)
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Course Price</span>
                    <span className="text-white font-medium">${Number(formData.price || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Platform Fee (20%)</span>
                    <span className="text-coral font-medium">-${(Number(formData.price || 0) * 0.20).toFixed(2)}</span>
                  </div>
                  <div className="h-px bg-white/10 my-2" />
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">Your Earnings</span>
                    <span className="text-emerald-400 font-bold text-lg">${(Number(formData.price || 0) * 0.80).toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-white/40 text-xs mt-4">
                  * Platform fee covers payment processing, hosting, and platform maintenance
                </p>
              </div>
            )}

            {/* Course Stats */}
            {course && course.totalStudents > 0 && (
              <div className="p-6 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20">
                <h4 className="font-semibold text-white mb-4">Lifetime Earnings Estimate</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">{course.totalStudents}</div>
                    <div className="text-sm text-white/50">Total Students</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">${Number(formData.price || 0).toFixed(2)}</div>
                    <div className="text-sm text-white/50">Price per Course</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-400">
                      ${(course.totalStudents * Number(formData.price || 0) * 0.80).toFixed(2)}
                    </div>
                    <div className="text-sm text-white/50">Est. Total Earnings</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="p-6 rounded-xl bg-coral/10 border border-coral/20">
              <h3 className="text-lg font-semibold text-white mb-2">Danger Zone</h3>
              <p className="text-white/50 mb-4">Archiving a course will hide it from students. This action can be undone.</p>
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to archive this course?')) {
                    try {
                      await coursesApi.delete(course.id);
                      toast.success('Course archived');
                      navigate('/professor/courses');
                    } catch (error) {
                      toast.error('Failed to archive course');
                    }
                  }
                }}
                className="px-4 py-2 rounded-xl bg-coral/20 text-coral hover:bg-coral/30 transition-colors flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                Archive Course
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Add Section Modal */}
      <AnimatePresence>
        {showAddSection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddSection(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl p-6"
              style={{
                background: 'linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <h3 className="text-xl font-semibold text-white mb-6">Add New Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="label">Section Title *</label>
                  <input
                    type="text"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    placeholder="e.g., Introduction to the Course"
                    className="input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="label">Description (optional)</label>
                  <textarea
                    value={newSectionDescription}
                    onChange={(e) => setNewSectionDescription(e.target.value)}
                    placeholder="Brief description of this section"
                    rows={3}
                    className="input resize-none"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAddSection(false)}
                  className="px-4 py-2.5 rounded-xl text-white/60 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSection}
                  disabled={!newSectionTitle.trim()}
                  className="btn-primary disabled:opacity-50"
                >
                  Add Section
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Lesson Modal */}
      <AnimatePresence>
        {showAddLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddLesson(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{
                background: 'linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <h3 className="text-xl font-semibold text-white mb-6">Add New Lesson</h3>
              <div className="space-y-4">
                <div>
                  <label className="label">Lesson Title *</label>
                  <input
                    type="text"
                    value={newLesson.title}
                    onChange={(e) => setNewLesson(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Welcome to the course"
                    className="input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="label">Description (optional)</label>
                  <textarea
                    value={newLesson.description}
                    onChange={(e) => setNewLesson(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this lesson"
                    rows={3}
                    className="input resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Type</label>
                    <select
                      value={newLesson.type}
                      onChange={(e) => setNewLesson(prev => ({ ...prev, type: e.target.value as 'VIDEO' | 'DOCUMENT' | 'QUIZ' }))}
                      className="input"
                    >
                      <option value="VIDEO">Video</option>
                      <option value="DOCUMENT">Document</option>
                      <option value="QUIZ">Quiz</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Duration (seconds)</label>
                    <input
                      type="number"
                      value={newLesson.duration}
                      onChange={(e) => setNewLesson(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                      min="0"
                      className="input"
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Content URL</label>
                  <input
                    type="url"
                    value={newLesson.contentUrl}
                    onChange={(e) => setNewLesson(prev => ({ ...prev, contentUrl: e.target.value }))}
                    placeholder="https://example.com/video.mp4"
                    className="input"
                  />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newLesson.isFree}
                    onChange={(e) => setNewLesson(prev => ({ ...prev, isFree: e.target.checked }))}
                    className="w-5 h-5 rounded border-white/20 bg-white/10 text-coral focus:ring-coral"
                  />
                  <span className="text-white/70">Make this lesson free (preview)</span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAddLesson(null)}
                  className="px-4 py-2.5 rounded-xl text-white/60 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAddLesson(showAddLesson)}
                  disabled={!newLesson.title.trim()}
                  className="btn-primary disabled:opacity-50"
                >
                  Add Lesson
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
